import type {
  App6EchelonCode,
  App6Entry,
  EniRole,
  OdbCheck,
  OdbIssue,
  OdbNode,
  OdbSymbol,
} from "../../api/types";
import { entryFor } from "./app6";
import { cleanNumber, effectiveHigher, NUMBER_RE } from "./numbering";
import { sidcOf } from "./sidc";
import {
  canHaveChildren,
  childEchelonAt,
  echelonAt,
  isEnemyKind,
  nodeAtOrNull,
  PLACED_RANK,
  type Path,
  pathKey,
  rankAt,
  sideOf,
  tfAt,
  type TreeContext,
  walk,
} from "./tree";

/** Ce que l'éditeur affiche de chaque unité : échelon, rang et symbole déduits dans le navigateur (affichage
 *  immédiat), code, rôle et place dans la manœuvre calculés par le serveur (aperçu aligné sur le brouillon), et
 *  contrôles qui la désignent. Fonctions pures. */

export interface UnitInfo {
  path: Path;
  key: string;
  node: OdbNode;
  echelon: App6EchelonCode;
  rank: string | null;
  tf: boolean;
  hq: boolean;
  sidc: string | null;
  number: string | null;
  higher: { value: string | null; auto: boolean };
  entry: App6Entry | null;
  /** Symbole du serveur (aperçu du brouillon affiché), ou null entre deux aperçus. */
  server: OdbSymbol | null;
  /** Code au format de l'iPad (ennemi : cité par l'ordre), du serveur. */
  code: string | null;
  role: EniRole | null;
  issues: OdbIssue[];
  childEchelon: App6EchelonCode | null;
  /** Rang placé par la manœuvre quand sa formation est commandée (S/GTIA, GTIA, brigades). */
  placedRank: boolean;
  /** Tient un emplacement de la manœuvre (le sien, sinon celui de la fonction). */
  manoeuvre: boolean;
}

/** Contrôles du serveur : structurés (`issues`), sinon les textes d'un serveur antérieur, sans chemin. */
export function issuesOf(check: Pick<OdbCheck, "issues" | "errors" | "warnings"> | null | undefined): OdbIssue[] {
  if (!check) return [];
  if (check.issues) return check.issues;
  return [
    ...check.errors.map((message): OdbIssue => ({ level: "error", code: "", message, path: null, field: null })),
    ...check.warnings.map((message): OdbIssue => ({ level: "warning", code: "", message, path: null, field: null })),
  ];
}

/** Numéros illisibles, signalés dès la frappe (mêmes messages que le serveur, E1 et E2). */
export function localIssues(tree: OdbNode): OdbIssue[] {
  const out: OdbIssue[] = [];
  walk(tree, (node, path) => {
    const name = node.name.trim() || node.code || "";
    const number = cleanNumber(node.number);
    if (number && !NUMBER_RE.test(number))
      out.push({
        level: "error",
        code: "E1",
        message: `Numéro de « ${name} » : chiffres seulement, quatre au plus (ex. 2).`,
        path: pathKey(path),
        field: "number",
      });
    const higher = cleanNumber(node.higher);
    if (higher && !NUMBER_RE.test(higher))
      out.push({
        level: "error",
        code: "E2",
        message: `Numéro de la formation supérieure de « ${name} » : chiffres seulement, quatre au plus (ex. 3).`,
        path: pathKey(path),
        field: "higher",
      });
  });
  return out;
}

/** Contrôles affichés : ceux du navigateur d'abord, puis ceux du serveur qui ne les répètent pas. */
export function mergeIssues(local: OdbIssue[], server: OdbIssue[]): OdbIssue[] {
  const seen = new Set(local.map((i) => `${i.code}|${i.path}|${i.field}`));
  return [...local, ...server.filter((i) => !seen.has(`${i.code}|${i.path}|${i.field}`))];
}

// Rangs placés par la manœuvre sous la tête de chaque nature amie (S/GTIA, GTIA, brigades ; table des échelons).
const PLACED_RANKS = new Set(Object.values(PLACED_RANK));

const LEVEL_ORDER = { error: 0, warning: 1, info: 2 } as const;

export function sortIssues(issues: OdbIssue[]): OdbIssue[] {
  return [...issues].sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);
}

/** Fiche de chaque unité de l'arbre, par clé de chemin. `aligned` : l'aperçu du serveur porte sur ce brouillon
 *  (ses chemins sont ceux de l'arbre affiché). */
export function describeUnits(
  tree: OdbNode,
  ctx: TreeContext,
  server: { symbols: readonly OdbSymbol[]; tree: OdbNode | null } | null,
  issues: readonly OdbIssue[],
): Map<string, UnitInfo> {
  const side = sideOf(ctx.kind);
  const enemy = isEnemyKind(ctx.kind);
  const symbols = new Map((server?.symbols ?? []).map((s) => [s.path, s]));
  const byPath = new Map<string, OdbIssue[]>();
  for (const issue of issues) if (issue.path) byPath.set(issue.path, [...(byPath.get(issue.path) ?? []), issue]);
  const out = new Map<string, UnitInfo>();
  walk(tree, (node, path) => {
    const key = pathKey(path);
    const echelon = echelonAt(tree, path, ctx);
    const rank = rankAt(tree, path, ctx);
    const tf = tfAt(tree, path, ctx);
    const hq = Boolean(node.hq);
    const symbol = symbols.get(key) ?? null;
    const reference = server ? nodeAtOrNull(server.tree, path) : null;
    const entry = entryFor(ctx.palette, node.fonction, node.app6);
    const sidc = sidcOf({ side, app6: node.app6, echelon, hq, tf }) ?? symbol?.sidc ?? null;
    const placedRank = !enemy && path.length > 0 && PLACED_RANKS.has(rank ?? "");
    out.set(key, {
      path,
      key,
      node,
      echelon,
      rank,
      tf,
      hq,
      sidc,
      number: cleanNumber(node.number),
      higher: effectiveHigher(tree, path),
      entry,
      server: symbol,
      code: reference?.code ?? node.code ?? null,
      role: node.role ?? reference?.role ?? null,
      issues: byPath.get(key) ?? [],
      childEchelon: canHaveChildren(echelon) ? childEchelonAt(tree, path, ctx) : null,
      placedRank,
      manoeuvre: node.manoeuvre ?? symbol?.manoeuvre ?? entry?.manoeuvre ?? true,
    });
  });
  return out;
}
